import type { JohnDeereMachine, JohnDeereMachineImportError } from "@services/johnDeereService";

/** Can be ticked at all: not on this account yet, and not on another one. */
export const isSelectable = (m: JohnDeereMachine): boolean => !m.imported && !m.importedElsewhere;

/**
 * What "Select all" ticks: selectable combines only. "Show all machines" also
 * lists tractors and sprayers, and a customer cannot remove a machine once it
 * is imported, so those are ticked one at a time or not at all.
 */
export const bulkSelectable = (visible: JohnDeereMachine[]): JohnDeereMachine[] =>
  visible.filter((m) => isSelectable(m) && m.combine);

/** True when every combine "Select all" would tick is already ticked. */
export const allBulkSelected = (selected: Set<string>, visible: JohnDeereMachine[]): boolean => {
  const bulk = bulkSelectable(visible);
  return bulk.length > 0 && bulk.every((m) => selected.has(m.equipmentId));
};

/**
 * Select all / Deselect all over the visible list. Deselect clears every
 * visible machine, including ones ticked by hand; select adds combines only.
 */
export const toggleAllSelection = (selected: Set<string>, visible: JohnDeereMachine[]): Set<string> => {
  const next = new Set(selected);
  if (allBulkSelected(selected, visible)) {
    visible.filter(isSelectable).forEach((m) => next.delete(m.equipmentId));
  } else {
    bulkSelectable(visible).forEach((m) => next.add(m.equipmentId));
  }
  return next;
};

export type MachineImportErrorLabels = {
  notFound: string;
  elsewhere: string;
  failed: string;
};

/**
 * Localised reason one machine was not imported. The backend's `error` is
 * English; it is shown only by a backend that sent no `code`.
 */
export const machineImportErrorText = (
  err: JohnDeereMachineImportError,
  labels: MachineImportErrorLabels
): string => {
  switch (err.code) {
    case "MACHINE_NOT_FOUND":
      return labels.notFound;
    case "IMPORTED_ELSEWHERE":
      return labels.elsewhere;
    case undefined:
      return err.error || labels.failed;
    default:
      return labels.failed;
  }
};
